import { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import DatePicker from '../../components/DatePicker';
import RezervasyonDetayModal from '../../components/RezervasyonDetayModal';
import UsageInfo from '../../components/UsageInfo';
import { getRezervasyonRaporu, getRezervasyonOzet, apiRequest } from '../../services/api';
import YilDropdown from '../../components/YilDropdown';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';

// ── Sabitler ──────────────────────────────────────────────────
var AYLAR   = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
var DURUMLAR = ['Hepsi','Check IN Bekliyor','C-IN Yapıldı','C-OUT Yapıldı','İptal','No-show'];

var DURUM_BADGE = {
  'Check IN Bekliyor': 'bg-blue-100 text-blue-700',
  'C-IN Yapıldı':      'bg-green-100 text-green-700',
  'C-OUT Yapıldı':     'bg-gray-100 text-gray-600',
  'İptal':             'bg-red-100 text-red-600',
  'No-show':           'bg-orange-100 text-orange-700',
};

var DAGILIM_RENKLER = ['#3b82f6','#22c55e','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316','#ec4899'];

function renderPieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.06) return null;
  var RADIAN = Math.PI / 180;
  var radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  var x = cx + radius * Math.cos(-midAngle * RADIAN);
  var y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
      fontSize={11} fontWeight="bold" style={{ pointerEvents: 'none' }}>
      {`%${(percent * 100).toFixed(0)}`}
    </text>
  );
}

function pad(n) { return String(n).padStart(2, '0'); }
function bugunStr()    { var d = new Date(); return pad(d.getDate())+'.'+pad(d.getMonth()+1)+'.'+d.getFullYear(); }
function ayBasi(y, m)  { return '01.'+pad(m+1)+'.'+y; }
function ayinSonu(y,m) { var s = new Date(y, m+1, 0); return pad(s.getDate())+'.'+pad(m+1)+'.'+y; }
function ddToIso(s)    { if (!s) return ''; var p = s.split('.'); return p[2]+'-'+p[1]+'-'+p[0]; }

// ── Ana Bileşen ───────────────────────────────────────────────
export default function RaporlamaRezervasyon() {
  var bugun = new Date();
  var [yil,  setYil]  = useState(bugun.getFullYear());
  var [ay,   setAy]   = useState(bugun.getMonth());
  var [bas,  setBas]  = useState(ayBasi(bugun.getFullYear(), bugun.getMonth()));
  var [bit,  setBit]  = useState(bugunStr());

  // Filtre state'leri
  var [durum,      setDurum]      = useState('Hepsi');
  var [odaTipi,    setOdaTipi]    = useState('Hepsi');
  var [adSoyad,    setAdSoyad]    = useState('');
  var [odaTipleri, setOdaTipleri] = useState([]);

  // Tetiklenmiş filtre (Filtrele butonuna basılınca güncellenir)
  var [aktifFiltre, setAktifFiltre] = useState(null);

  // Veri state'leri
  var [satirlar,   setSatirlar]   = useState([]);
  var [ozet,       setOzet]       = useState(null);
  var [toplamKayit, setToplamKayit] = useState(0);
  var [toplamSayfa, setToplamSayfa] = useState(1);
  var [sayfa,      setSayfa]      = useState(1);
  var [loading,    setLoading]    = useState(false);
  var [ozetLoading, setOzetLoading] = useState(false);
  var [error,      setError]      = useState(null);

  // Sıralama
  var [sortK, setSortK] = useState('girisTarihi');
  var [sortD, setSortD] = useState('desc');

  // Detay modal
  var [detay, setDetay] = useState(null);

  // Tablo kolon arama
  var [kolAra, setKolAra] = useState({});

  // ── Filtrele ──
  function filtrele() {
    setSayfa(1);
    setAktifFiltre({ bas, bit, durum, odaTipi });
  }

  function temizle() {
    setBas(ayBasi(yil, ay)); setBit(bugunStr());
    setDurum('Hepsi'); setOdaTipi('Hepsi'); setAdSoyad('');
    setKolAra({});
    setSayfa(1);
    setAktifFiltre(null);
  }

  // ── Ay seç ──
  function secAy(i) {
    setAy(i);
    setBas(ayBasi(yil, i));
    setBit(ayinSonu(yil, i));
  }

  // ── Sıralama ──
  function sort(k) {
    if (sortK === k) setSortD(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortK(k); setSortD('asc'); }
  }
  function sIkon(k) {
    if (sortK !== k) return <span className="text-gray-300 ml-1">⇅</span>;
    return <span className="text-blue-500 ml-1">{sortD === 'asc' ? '↑' : '↓'}</span>;
  }

  // ── API çağrısı: tablo ──
  var fetchRapor = useCallback(async (f, s) => {
    setLoading(true);
    setError(null);
    try {
      var baslangic = ddToIso(f ? f.bas : bas);
      var bitis     = ddToIso(f ? f.bit : bit);
      var res = await getRezervasyonRaporu({
        baslangic,
        bitis,
        durum:   f ? f.durum   : durum,
        odaTipi: f ? f.odaTipi : odaTipi,
        sayfa:   s ?? sayfa,
        sayfaBoyutu: 50,
      });
      if (res) {
        setSatirlar(res.satirlar ?? []);
        setToplamKayit(res.toplamKayit ?? 0);
        setToplamSayfa(res.toplamSayfa ?? 1);
      }
    } catch {
      setError('Veriler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [bas, bit, durum, odaTipi, sayfa]);

  // ── API çağrısı: özet ──
  var fetchOzet = useCallback(async (f) => {
    setOzetLoading(true);
    try {
      var baslangic = ddToIso(f ? f.bas : bas);
      var bitis     = ddToIso(f ? f.bit : bit);
      var res = await getRezervasyonOzet(baslangic, bitis);
      if (res) setOzet(res);
    } catch {
      setOzet(null);
    } finally {
      setOzetLoading(false);
    }
  }, [bas, bit]);

  // Oda tipleri
  useEffect(() => {
    apiRequest('OdaTipi').then(data => setOdaTipleri(data || [])).catch(() => {});
  }, []);

  // İlk yükleme
  useEffect(() => {
    fetchRapor(null, 1);
    fetchOzet(null);
  }, []); // eslint-disable-line

  // Filtre tetiklenince
  useEffect(() => {
    if (aktifFiltre === null) return;
    fetchRapor(aktifFiltre, 1);
    fetchOzet(aktifFiltre);
  }, [aktifFiltre]); // eslint-disable-line

  // Sayfa değişince
  useEffect(() => {
    if (sayfa === 1) return;
    fetchRapor(aktifFiltre, sayfa);
  }, [sayfa]); // eslint-disable-line

  // ── Client-side ad filtresi + sıralama ──
  var goruntulenen = satirlar
    .filter(r => {
      if (!adSoyad) return true;
      return r.misafirAdlari?.toLocaleLowerCase('tr-TR').includes(adSoyad.toLocaleLowerCase('tr-TR'));
    })
    .filter(r => {
      return Object.entries(kolAra).every(([k, v]) => {
        if (!v) return true;
        var val = String(r[k] ?? '').toLocaleLowerCase('tr-TR');
        return val.includes(v.toLocaleLowerCase('tr-TR'));
      });
    })
    .sort((a, b) => {
      var av = a[sortK] ?? '', bv = b[sortK] ?? '';
      if (sortK === 'odaTutari' || sortK === 'kalan' || sortK === 'toplamOdeme') {
        av = parseFloat(av) || 0; bv = parseFloat(bv) || 0;
      } else if (sortK === 'geceSayisi') {
        av = parseInt(av) || 0; bv = parseInt(bv) || 0;
      }
      if (av < bv) return sortD === 'asc' ? -1 : 1;
      if (av > bv) return sortD === 'asc' ? 1 : -1;
      return 0;
    });

  var aktifler    = goruntulenen.filter(r => r.durum !== 'İptal' && r.durum !== 'No-show');
  var toplamTutar = aktifler.reduce((a, r) => a + (r.odaTutari || 0), 0);
  var toplamGece  = aktifler.reduce((a, r) => a + (r.geceSayisi || 0), 0);

  // ── Excel export ──
  function excelExport() {
    var basliklar = ['Rez.No','Misafirler','Oda','Oda Tipi','Giriş','G.Saat','Çıkış','Ç.Saat','Gece','Acente','Tutar','PB','Ödeme','Kalan','Durum','Personel'];
    var satirlar = goruntulenen.map(r => [
      r.rezNo, r.misafirAdlari, r.odaNo, r.odaTipiAd,
      r.girisTarihi, r.girisSaati, r.cikisTarihi, r.cikisSaati,
      r.geceSayisi, r.acenteAd,
      r.odaTutari, r.paraBirimi, r.toplamOdeme, r.kalan,
      r.durum, r.personelAd,
    ]);
    downloadExcel('rezervasyon-raporu', 'Rezervasyonlar', basliklar, satirlar);
  }

  function yazdir() {
    var basliklar = ['Rez.No','Misafirler','Oda','Oda Tipi','Giriş','Çıkış','Gece','Acente','Tutar','PB','Durum'];
    var satirlar = goruntulenen.map(r => [r.rezNo, r.misafirAdlari, r.odaNo, r.odaTipiAd, r.girisTarihi, r.cikisTarihi, r.geceSayisi, r.acenteAd, r.odaTutari, r.paraBirimi, r.durum]);
    printTable('Rezervasyon Raporu', basliklar, satirlar, goruntulenen.length + ' rezervasyon');
  }

  var SEL = "border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none appearance-none pr-7 cursor-pointer";
  var INP = "border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none";

  return (
    <div className="p-3 md:p-6 space-y-8 min-h-screen bg-gray-50/50" style={{ animation: 'fadeIn 0.3s ease-out' }}>

      <PageHeader
        icon="📋"
        iconColor="from-blue-500 to-indigo-700"
        iconShadow="shadow-blue-200"
        title="Rezervasyon Analizi"
        subtitle={`${AYLAR[ay]} ${yil} — Rezervasyon ve Kanal Performansı`}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <YilDropdown value={yil} onChange={y => setYil(y)} />
          <div className="relative group">
            <select value={ay} onChange={e => secAy(Number(e.target.value))}
              className="bg-white border-2 border-gray-100 hover:border-gray-800 rounded-2xl px-5 py-3 pr-10 text-sm font-black text-gray-700 appearance-none cursor-pointer transition-all uppercase tracking-widest">
              {AYLAR.map((a, i) => <option key={a} value={i}>{a}</option>)}
            </select>
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▼</span>
          </div>
        </div>
      </PageHeader>

      {/* Akıllı Filtre Paneli */}
      <div className="bg-white rounded-[2rem] border border-gray-100 shadow-xl p-6 md:p-8 z-20 relative">
        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Detaylı Filtreleme & Arama</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 tracking-widest ml-1">Giriş Başlangıç</label>
            <DatePicker value={bas} onChange={setBas} maxDate={bit} />
          </div>
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 tracking-widest ml-1">Giriş Bitiş</label>
            <DatePicker value={bit} onChange={setBit} minDate={bas} />
          </div>
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 tracking-widest ml-1">Rezervasyon Durumu</label>
            <div className="relative">
              <select value={durum} onChange={e => setDurum(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 font-semibold text-sm text-gray-700 focus:outline-none focus:border-blue-400 appearance-none cursor-pointer">
                {DURUMLAR.map(v => <option key={v}>{v}</option>)}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▼</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 tracking-widest ml-1">Oda Tipi</label>
            <div className="relative">
              <select value={odaTipi} onChange={e => setOdaTipi(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 font-semibold text-sm text-gray-700 focus:outline-none focus:border-blue-400 appearance-none cursor-pointer">
                <option value="Hepsi">Tüm Oda Tipleri</option>
                {odaTipleri.map(t => <option key={t.odaTipiId} value={t.ad}>{t.ad}</option>)}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▼</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-end h-full">
            <button onClick={filtrele} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-blue-100 transition-all active:scale-95">Raporla</button>
            <button onClick={temizle} className="bg-gray-100 hover:bg-gray-200 text-gray-500 px-4 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all">Sıfırla</button>
          </div>
        </div>
      </div>

      {/* Hata */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-6 py-4 text-sm font-bold shadow-lg shadow-red-50/50 flex items-center gap-3">
          <span className="text-xl">⚠️</span> {error}
        </div>
      )}

      {/* Özet Kartlar */}
      {ozetLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-[1.5rem] p-5 animate-pulse h-24" />
          ))}
        </div>
      ) : ozet && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 relative z-10">
          {[
            { label: 'TOPLAM', value: ozet.toplamRezervasyonSayisi, bg: 'bg-gradient-to-br from-blue-50 to-blue-100', text: 'text-blue-700', icon: '📋' },
            { label: 'AKTİF SOL', value: ozet.aktifSayisi, bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100', text: 'text-emerald-700', icon: '✅' },
            { label: 'İPTAL', value: ozet.iptalSayisi, bg: 'bg-gradient-to-br from-red-50 to-red-100', text: 'text-red-700', icon: '❌' },
            { label: 'NO-SHOW', value: ozet.noShowSayisi, bg: 'bg-gradient-to-br from-orange-50 to-orange-100', text: 'text-orange-700', icon: '🚫' },
            { label: 'TOPLAM GELİR', value: ozet.toplamGelir?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺', bg: 'bg-gradient-to-br from-purple-50 to-purple-100', text: 'text-purple-700', icon: '💰' },
            { label: 'KALAN TAHSİLAT', value: ozet.toplamKalan?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' ₺', bg: 'bg-gradient-to-br from-amber-50 to-amber-100', text: 'text-amber-700', icon: '🔑' },
          ].map(k => (
            <div key={k.label} className={`${k.bg} rounded-[2rem] p-5 border-2 border-white shadow-xl transition-all hover:-translate-y-1 group relative overflow-hidden`}>
              <div className="absolute -right-4 -bottom-4 text-6xl opacity-5 group-hover:scale-125 transition-transform duration-500">{k.icon}</div>
              <div className={`text-xl md:text-2xl font-black ${k.text} tracking-tighter truncate leading-none`}>{k.value ?? 0}</div>
              <div className={`text-[9px] font-black ${k.text} opacity-60 uppercase tracking-widest mt-2`}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Grafikler */}
      {ozet && (ozet.odaTipiDagilimi?.length > 0 || ozet.kanalDagilimi?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Oda Tipi Dağılımı */}
          {ozet.odaTipiDagilimi?.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-xl relative overflow-hidden group transition-all hover:border-blue-200">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full -mr-10 -mt-10 blur-3xl opacity-60"></div>
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Oda Tipi Dağılımı</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={ozet.odaTipiDagilimi} dataKey="sayi" nameKey="ad" cx="50%" cy="50%" outerRadius={85} paddingAngle={5}
                    label={renderPieLabel} labelLine={false}>
                    {ozet.odaTipiDagilimi.map((_, i) => (
                      <Cell key={i} fill={DAGILIM_RENKLER[i % DAGILIM_RENKLER.length]} strokeWidth={0} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ borderRadius:'1.5rem', border:'none', boxShadow:'0 20px 25px -5px rgba(0,0,0,0.1)', padding:'1rem' }}
                    formatter={(v, n, p) => {
                      var toplam = ozet.odaTipiDagilimi.reduce((s, d) => s + d.sayi, 0);
                      var pct = toplam > 0 ? ((v / toplam) * 100).toFixed(1) : 0;
                      return [`${v} Rez. (%${pct})`, p.payload.ad];
                    }} 
                  />
                  <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ paddingTop:'20px' }} formatter={(v) => <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          {/* Kanal Dağılımı */}
          {ozet.kanalDagilimi?.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-xl relative overflow-hidden group transition-all hover:border-emerald-200">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-full -mr-10 -mt-10 blur-3xl opacity-60"></div>
              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Kanal / Acente Dağılımı</h3>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={ozet.kanalDagilimi} dataKey="sayi" nameKey="ad" cx="50%" cy="50%" outerRadius={85} paddingAngle={5}
                    label={renderPieLabel} labelLine={false}>
                    {ozet.kanalDagilimi.map((_, i) => (
                      <Cell key={i} fill={DAGILIM_RENKLER[i % DAGILIM_RENKLER.length]} strokeWidth={0} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ borderRadius:'1.5rem', border:'none', boxShadow:'0 20px 25px -5px rgba(0,0,0,0.1)', padding:'1rem' }}
                    formatter={(v, n, p) => {
                      var toplam = ozet.kanalDagilimi.reduce((s, d) => s + d.sayi, 0);
                      var pct = toplam > 0 ? ((v / toplam) * 100).toFixed(1) : 0;
                      return [`${v} Rez. (%${pct})`, p.payload.ad];
                    }} 
                  />
                  <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ paddingTop:'20px' }} formatter={(v) => <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Tablo Modülü */}
      <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl overflow-hidden relative z-10">
        <div className="px-8 py-6 border-b border-gray-50 bg-gray-50/30 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-2 h-8 bg-blue-500 rounded-full"></div>
            <div>
              <p className="text-sm font-black text-gray-900 uppercase tracking-tighter">Rezervasyon Listesi</p>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Toplam {toplamKayit} Kayıt Bulundu</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap w-full sm:w-auto">
             <input value={adSoyad} onChange={e => setAdSoyad(e.target.value)} className="bg-white border border-gray-200 rounded-xl px-4 py-2 text-xs font-semibold focus:outline-none focus:border-blue-400 w-full sm:w-48 shadow-sm" placeholder="Tabloda Misafir Ara..." />
             <button onClick={excelExport} className="flex-1 sm:flex-none bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-black px-5 py-2.5 rounded-xl shadow-lg shadow-emerald-100 transition-all uppercase tracking-widest whitespace-nowrap"><span>📊</span> Excel İndir</button>
             <button onClick={yazdir} className="flex-1 sm:flex-none bg-white border border-gray-200 hover:bg-gray-800 hover:text-white text-gray-700 text-[10px] font-black px-5 py-2.5 rounded-xl shadow-lg transition-all uppercase tracking-widest whitespace-nowrap"><span>🖨️</span> Yazdır</button>
          </div>
        </div>

      {/* Tablo – Mobil Kartlar */}
      <div className="md:hidden space-y-2">
        {loading ? (
          <LoadingSpinner fullPage text="Yükleniyor..." color="blue" />
        ) : goruntulenen.length === 0 ? (
          <EmptyState icon="📋" text="Kayıt bulunamadı" />
        ) : goruntulenen.map(r => (
          <div key={r.rezervasyonId} className="bg-white border border-gray-200 rounded-xl p-3">
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="font-mono text-xs font-bold text-gray-500">{r.rezNo}</span>
                <p className="font-semibold text-gray-800 text-sm">{r.misafirAdlari || '—'}</p>
              </div>
              <span className={'text-xs font-bold px-2 py-1 rounded-full ' + (DURUM_BADGE[r.durum] || 'bg-gray-100 text-gray-600')}>{r.durum}</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 mb-2">
              <div><span className="text-gray-400">Oda: </span><strong>{r.odaNo}</strong> <span className="text-gray-400">{r.odaTipiAd}</span></div>
              <div><span className="text-gray-400">Gece: </span><strong>{r.geceSayisi}</strong></div>
              <div><span className="text-gray-400">Giriş: </span>{r.girisTarihi}</div>
              <div><span className="text-gray-400">Çıkış: </span>{r.cikisTarihi}</div>
              <div className="col-span-2">
                <span className="text-gray-400">Tutar: </span>
                <strong>{(r.odaTutari||0).toLocaleString('tr-TR',{minimumFractionDigits:2})} {r.paraBirimi||'TL'}</strong>
                {r.acenteAd ? <span className="ml-2 text-gray-400">· {r.acenteAd}</span> : null}
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={() => setDetay(r)} className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg">Detay</button>
            </div>
          </div>
        ))}
      </div>

      {/* Tablo – Masaüstü */}
      <div className="hidden md:block bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {[
                ['rezNo',        'Rez. No'],
                ['misafirAdlari','Misafirler'],
                ['odaNo',        'Oda'],
                ['odaTipiAd',    'Tip'],
                ['girisTarihi',  'Giriş'],
                ['girisSaati',   'G.Saat'],
                ['cikisTarihi',  'Çıkış'],
                ['cikisSaati',   'Ç.Saat'],
                ['geceSayisi',   'Gece'],
                ['acenteAd',     'Acente'],
                ['odaTutari',    'Tutar'],
                ['paraBirimi',   'PB'],
                ['durum',        'Durum'],
                ['personelAd',   'Personel'],
                ['_detay',       'Detay'],
              ].map(([k, label]) => (
                <th key={k}
                  onClick={k !== '_detay' && k !== 'paraBirimi' ? () => sort(k) : undefined}
                  className={'px-3 py-3 text-left font-semibold text-gray-600 whitespace-nowrap ' + (k !== '_detay' && k !== 'paraBirimi' ? 'cursor-pointer' : '')}>
                  {label} {k !== '_detay' && k !== 'paraBirimi' && sIkon(k)}
                </th>
              ))}
            </tr>
            {/* Kolon arama satırı */}
            <tr className="bg-gray-50 border-b border-gray-200">
              {['rezNo','misafirAdlari','odaNo','odaTipiAd','girisTarihi','girisSaati','cikisTarihi','cikisSaati','geceSayisi','acenteAd','odaTutari','','durum','personelAd',''].map((k, i) => (
                <td key={i} className="px-1 py-1">
                  {k ? (
                    <input
                      value={kolAra[k] || ''}
                      onChange={e => setKolAra(prev => ({ ...prev, [k]: e.target.value }))}
                      placeholder="..."
                      className="w-full border-0 border-b border-gray-200 px-2 py-1 text-xs focus:outline-none bg-gray-50 placeholder-gray-300"
                    />
                  ) : null}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <EmptyState as="td" colSpan={15} icon="⏳" text="Yükleniyor..." />
            ) : goruntulenen.length === 0 ? (
              <EmptyState as="td" colSpan={15} icon="📋" text="Kayıt bulunamadı" />
            ) : goruntulenen.map(r => (
              <tr key={r.rezervasyonId} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2.5 font-mono text-xs font-semibold text-gray-700">{r.rezNo}</td>
                <td className="px-3 py-2.5 text-gray-700 max-w-[140px] truncate" title={r.misafirAdlari}>{r.misafirAdlari || '—'}</td>
                <td className="px-3 py-2.5 font-semibold text-gray-800">{r.odaNo}</td>
                <td className="px-3 py-2.5 text-gray-600 text-xs">{r.odaTipiAd}</td>
                <td className="px-3 py-2.5 text-gray-700">{r.girisTarihi}</td>
                <td className="px-3 py-2.5 text-gray-500">{r.girisSaati}</td>
                <td className="px-3 py-2.5 text-gray-700">{r.cikisTarihi}</td>
                <td className="px-3 py-2.5 text-gray-500">{r.cikisSaati}</td>
                <td className="px-3 py-2.5 font-semibold text-center">{r.geceSayisi}</td>
                <td className="px-3 py-2.5 text-gray-600">{r.acenteAd}</td>
                <td className="px-3 py-2.5 font-semibold text-gray-800">{(r.odaTutari || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">{r.paraBirimi || 'TL'}</td>
                <td className="px-3 py-2.5">
                  <span className={'text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap ' + (DURUM_BADGE[r.durum] || 'bg-gray-100 text-gray-600')}>
                    {r.durum}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-gray-600">{r.personelAd}</td>
                <td className="px-3 py-2.5">
                  <button
                    onClick={() => setDetay(r)}
                    className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg">
                    Detay
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          {!loading && goruntulenen.length > 0 && (
            <tfoot>
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold">
                <td colSpan={8} className="px-3 py-2.5 text-gray-700" />
                <td className="px-3 py-2.5 text-center text-gray-700">{toplamGece} gece</td>
                <td className="px-3 py-2.5" />
                <td className="px-3 py-2.5 text-gray-900">{toplamTutar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>

      {/* Pagination */}
      {toplamSayfa > 1 && (
        <div className="flex items-center justify-center gap-3 pt-6 pb-2">
          <button
            onClick={() => setSayfa(s => Math.max(1, s - 1))}
            disabled={sayfa === 1 || loading}
            className="flex items-center gap-2 px-6 py-3 rounded-2xl border border-gray-200 text-[10px] font-black uppercase tracking-widest text-gray-600 hover:bg-gray-800 hover:text-white disabled:opacity-30 transition-all">
            ← Önceki Sayfa
          </button>
          <div className="bg-white border border-gray-200 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest text-blue-600 shadow-sm">
            {sayfa} / {toplamSayfa}
          </div>
          <button
            onClick={() => setSayfa(s => Math.min(toplamSayfa, s + 1))}
            disabled={sayfa === toplamSayfa || loading}
            className="flex items-center gap-2 px-6 py-3 rounded-2xl border border-gray-200 text-[10px] font-black uppercase tracking-widest text-gray-600 hover:bg-gray-800 hover:text-white disabled:opacity-30 transition-all">
            Sonraki Sayfa →
          </button>
        </div>
      )}

      {/* UsageInfo */}
      <UsageInfo 
        color="blue" 
        description="Rezervasyon Analizi, otelinizin satış kanalları, doluluk trendleri ve finansal tahsilat durumunu kuşbakışı incelemenizi sağlar. Geçmiş verilere bakarak gelecekteki fiyatlandırma stratejinizi belirleyebilirsiniz." 
        scenarios={[
          { title: "Kanal Karlılığı Analizi", content: "Kanal / Acente dağılımı grafiğine bakarak hangi OTA (Booking, Expedia vb.) veya acenteden daha çok rezervasyon aldığınızı görün. Komisyon oranlarını da ekleyerek gerçek kâr marjınızı hesaplayabilirsiniz." },
          { title: "Tahsilat Takibi", content: "Özet kartlardaki 'Kalan Tahsilat' verisi, otelde konaklaması devam eden veya gelecek olan misafirlerden beklenen net nakit akışını gösterir. Finansal planlama için kritik bir veridir." },
          { title: "Oda Tipi Dağılımı (Upselling Fırsatı)", content: "Grafikte standart odaların çok yoğun, suitlerin ise boş olduğunu görüyorsanız, check-in sırasında misafirlerinize bir 'Upgrade' teklifi sunarak suit kullanımını ve gelirinizi artırabilirsiniz." }
        ]}
      />

      {/* Detay Modal */}
      {detay && (
        <RezervasyonDetayModal
          rezervasyonId={detay.rezervasyonId}
          onClose={() => setDetay(null)}
          onGuncelle={() => { setDetay(null); fetchRapor(aktifFiltre, sayfa); }}
        />
      )}

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  );
}
